/*
 * zeroLabs: Flaschen zum Drehen (Labor Produktseite, Richtung B "Der Name", docs/ideen.md).
 * Aus dem Konzept-Prototyp (Artefakt "Produktseite: Konzepte") ins Theme übernommen, ohne fremde Bibliothek.
 *
 * Eine Fläche (canvas), darin eine oder mehrere Flaschen, jede in ihrem eigenen Ausschnitt.
 * Glas, Kronkorken und Licht kommen 1:1 aus den freigestellten Packshots und bleiben stehen.
 * Etikett und Kronkorken drehen sich: vorn aus dem Packshot, an den Seiten und hinten aus den Druckdaten.
 * Text hinter der Fläche (der Name) scheint durch Glas und Bier, gebrochen wie durch einen Zylinder voll Bier.
 *
 * Bilder: Packshot als JPG plus Maske (PNG), Etikett als JPG plus ein Streifen für die Glas-Lücken.
 * So sind alle fünf Flaschen zusammen rund 2 MB statt 4 MB. Geladen wird nur, was gezeigt wird.
 *
 * window.zlFlasche.Reihe(canvas, { bilder, hilfen, griff })
 *   setze(slots)     slots: [{ sorte, x, y, h, dreh }] in CSS-Pixeln relativ zur Fläche, hinten zuerst
 *   drehe(winkel)    alle Flaschen gemeinsam drehen
 *   hinterText(el)   Element mit dem Text hinter der Fläche, eine Zeile oder mehrere [data-hinten-zeile]
 *   lade(sorten)     Bilder dieser Sorten vorab laden
 *
 * Finger: nur waagerechtes Ziehen dreht (touch-action: pan-y an der Griff-Fläche), Scrollen bleibt frei.
 * Alle Listener sind passiv. Bei "Bewegung reduzieren" kein Schwung nach dem Loslassen.
 */
(() => {
  'use strict';

  /* Maße des Packshots in Pixeln (480 x 1384) und die Bereiche darin */
  /* LAB1: das Etikett im Foto endet erst bei 1188 (Übergang 1186 bis 1188). Bei 1185 blieb zwischen Vorder- und Rückenetikett
     unten ein dünner Strich vom Foto stehen (Moritz, 05.10.2026), deshalb 1189 */
  /* GLAS_A, GLAS: Zeilen, aus denen das Glas zwischen den Etiketten kommt, direkt über und unter dem Etikett. Vorher 612 und 1215:
     1215 liegt auf einem dunkleren Ring der Flasche, die Lücke wirkte dadurch anders als das Glas gleich darunter (Moritz) */
  const BW = 480, BH = 1384, LAB0 = 632, LAB1 = 1189, GLAS = 1195, GLAS_A = 626, CX = 239.5, CAP0 = 14, CAP1 = 56;

  const VS = 'attribute vec2 p; varying vec2 vUv; void main(){ vUv = vec2(p.x*0.5+0.5, 0.5-p.y*0.5); gl_Position = vec4(p,0.,1.); }';
  const FS = [
    'precision highp float;',
    'varying vec2 vUv;',
    'uniform sampler2D tFlasche, tEtikett, tProfil, tLicht, tKappe, tKappeLicht, tHinten;',
    'uniform float uDreh, uLab0, uLab1, uCx, uGlas, uGlasA, uCap0, uCap1, uHinten, uLinse, uKappe, uWisch;',
    'uniform vec4 uHR;',
    /* ein Punkt auf dem Kronkorken: Winkel auf dem Zylinder zu Pixel im Foto */
    'vec3 kap(float th, float v, float h){ return texture2D(tFlasche, vec2(uCx + h * sin(clamp(th, -1.55, 1.55)), v)).rgb; }',
    /* Das Relief einer Zacke (tKappe) rundum, mit demselben Winkel wie das Etikett, mal dem stehenden Licht
       (Foto gemittelt über eine Zacke). Verwischt über den Sprung je gezeigtem Bild (Breite w in Zacken). */
    'vec3 kapRelief(float th0, float v, float h, float P, float w){',
    '  vec3 l = vec3(0.0);',
    '  for (int k = 0; k < 6; k++) l += kap(th0 + (float(k) - 2.5) / 6.0 * P, v, h);',
    '  float zr = (v - uCap0) / (uCap1 - uCap0), phi = (th0 - uDreh) / P;',
    '  vec3 r = vec3(0.0);',
    '  for (int k = 0; k < 8; k++) r += texture2D(tKappe, vec2(phi + (float(k) - 3.5) / 8.0 * w, zr)).rgb;',
    '  return l / 6.0 * r * (2.0 / 8.0);',
    '}',
    'void main(){',
    '  vec4 b = texture2D(tFlasche, vUv);',
    '  vec3 col = b.rgb;',
    '  float v = vUv.y;',
    '  float deckt = 0.0;',
    /* Kronkorken: dreht mit, wenn uKappe 1 ist, sonst steht er wie im Foto. Ein Kronkorken hat 21 Zacken, im Foto sind sie
       nur zu etwa 40 % gleich. Das Foto zu verschieben und von Zacke zu Zacke überzublenden sah deshalb bei jedem Tempo
       unnatürlich aus: langsam ein Morphen alle 17 Grad, schnell zitternde Lichter und ab einer halben Zacke je Bild der
       Wagenrad-Effekt (Moritz). Darum bei jedem Tempo, auch im Stand, ein rundum gleiches Relief aus einer Zacke des Fotos
       (scripts/zl-kappe-relief.py), gedreht mit demselben Winkel wie das Etikett, das Licht bleibt stehen. Ab etwa einer viertel
       Zacke je gezeigtem Bild verwischt es, ab knapp einer halben ganz. Gemessen bei 60 und 120 Hz und mit ausgelassenen Bildern. */
    '  if (v > uCap0 && v < uCap1) {',
    '    float h = texture2D(tProfil, vec2(v, 0.5)).r * 0.5;',
    '    float s = (vUv.x - uCx) / max(h, 0.0001);',
    '    if (abs(s) < 1.0) {',
    '      if (uKappe > 0.5) {',
    '        float th0 = asin(clamp(s, -1.0, 1.0));',
    '        float P = 6.2831853 / 21.0;',
    '        float x = abs(uWisch) / P;',
    '        vec3 c = kapRelief(th0, v, h, P, min(1.0, x + max(0.0, x - 0.25) * 2.75));',
    /* Nur die äußerste Kante (Kantenglättung des Fotos) kommt aus dem Foto. Vorher ab 82 % des Radius: dort standen die Zacken
       des Fotos still, während sie in der Mitte liefen (Moritz: unnatürlich) */
    '        col = mix(c, b.rgb, smoothstep(0.965, 0.995, abs(s)));',
    '      }',
    '      deckt = 1.0;',
    '    }',
    '  }',
    /* Etikett: rundum, zwischen Vorder- und Rückenetikett bleibt Glas */
    '  if (v > uLab0 && v < uLab1) {',
    '    float h = texture2D(tProfil, vec2(v, 0.5)).r * 0.5;',
    '    float s = (vUv.x - uCx) / max(h, 0.0001);',
    '    if (abs(s) < 1.0) {',
    '      float th = asin(clamp(s, -1.0, 1.0)) - uDreh;',
    '      float u = fract((th + 1.5707963) / 6.2831853);',
    '      vec2 eu = vec2(u, (v - uLab0) / (uLab1 - uLab0));',
    '      vec4 et = texture2D(tEtikett, eu);',
    '      float g = texture2D(tLicht, vec2((s + 1.0) * 0.5, 0.5)).r * 2.0;',
    '      float tv = (v - uLab0) / (uLab1 - uLab0);',
    '      vec3 glas = mix(texture2D(tFlasche, vec2(vUv.x, uGlasA)).rgb, texture2D(tFlasche, vec2(vUv.x, uGlas)).rgb, tv);',
    '      vec3 c = mix(glas, et.rgb * g, et.a); deckt = et.a;',
    /* Ganz am Rand der Flasche nicht das Foto: dort steht im Packshot das Vorderetikett, beim Drehen blieb es als farbiger Streifen
       stehen (Moritz). Stattdessen der Rand aus dem, was gerade dort ist: Glas wie über und unter dem Etikett oder das Etikett, abgedunkelt */
    '      vec3 rand = mix(glas, et.rgb * g * 0.55, et.a);',
    '      col = mix(c, rand, smoothstep(0.975, 0.998, abs(s)));',
    '    }',
    '  }',
    /* Schrift hinter der Flasche, gebrochen: zur Mitte gestreckt, zum Rand gestaucht, leicht gewellt und weich.
       Etikett und Kronkorken decken, dunkles Glas zeigt weniger, am Rand der Flasche verschwindet sie. */
    '  if (uHinten > 0.0 && b.a > 0.5) {',
    '    float hp = texture2D(tProfil, vec2(v, 0.5)).r * 0.5;',
    '    float sp = clamp((vUv.x - uCx) / max(hp, 0.0001), -1.0, 1.0);',
    '    float g = uLinse * sp + (1.18 - uLinse) * sp * sp * sp;',
    '    float vx = uCx + hp * g;',
    '    float vy = v + 0.0028 * sin(sp * 7.0 + v * 52.0);',
    '    float m = 0.0;',
    '    m += texture2D(tHinten, uHR.xy + vec2(vx, vy) * uHR.zw).a * 0.30;',
    '    m += texture2D(tHinten, uHR.xy + vec2(vx - 0.007, vy) * uHR.zw).a * 0.2;',
    '    m += texture2D(tHinten, uHR.xy + vec2(vx + 0.007, vy) * uHR.zw).a * 0.2;',
    '    m += texture2D(tHinten, uHR.xy + vec2(vx, vy - 0.003) * uHR.zw).a * 0.15;',
    '    m += texture2D(tHinten, uHR.xy + vec2(vx, vy + 0.003) * uHR.zw).a * 0.15;',
    '    float hell = smoothstep(0.04, 0.42, dot(col, vec3(0.299, 0.587, 0.114)));',
    '    float rand = 1.0 - smoothstep(0.78, 0.97, abs(sp));',
    '    col *= 1.0 - m * 0.46 * hell * rand * (1.0 - deckt) * uHinten;',
    '  }',
    /* Der Schatten im Packshot reicht bis an den Bildrand: außerhalb der Flasche läuft er zu den Rändern weich aus */
    '  float a = b.a;',
    '  float hs = texture2D(tProfil, vec2(v, 0.5)).r * 0.5;',
    '  float aussen = smoothstep(hs + 0.006, hs + 0.02, abs(vUv.x - uCx));',
    '  float rand2 = smoothstep(0.0, 0.22, vUv.x) * smoothstep(0.0, 0.22, 1.0 - vUv.x) * smoothstep(0.0, 0.012, 1.0 - v);',
    '  a *= mix(1.0, rand2, aussen);',
    '  gl_FragColor = vec4(col * a, a);',
    '}'
  ].join('\n');

  const bilder = {};
  const ladeBild = (url) => {
    if (!bilder[url]) bilder[url] = new Promise((ok, nein) => {
      const i = new Image(); i.crossOrigin = 'anonymous'; i.decoding = 'async';
      i.onload = () => ok(i); i.onerror = nein; i.src = url;
    });
    return bilder[url];
  };
  /* Farbe (JPG) und Deckkraft (Maske) zu einem Bild zusammensetzen */
  const zusammen = (farbe, maske) => {
    const c = document.createElement('canvas'); c.width = farbe.naturalWidth; c.height = farbe.naturalHeight;
    const g = c.getContext('2d');
    g.drawImage(farbe, 0, 0);
    g.globalCompositeOperation = 'destination-in';
    g.drawImage(maske, 0, 0, c.width, c.height);
    return c;
  };

  /* Text hinter der Fläche in eine Maske zeichnen, so groß wie die Fläche. Graue Zeilen scheinen schwächer durch. */
  function hintenMaske(self, el, c) {
    if (!el || !c.width) { self.hinten = 0; return; }
    let zeilen = el.querySelectorAll('[data-hinten-zeile]');
    zeilen = zeilen.length ? Array.from(zeilen) : [el];
    const rc = c.getBoundingClientRect();
    let sig = [rc.left, rc.top, rc.width, c.width, document.fonts ? document.fonts.status : ''];
    const info = zeilen.map((z) => {
      const re = z.getBoundingClientRect(), cs = getComputedStyle(z), txt = z.textContent.trim();
      const o = parseFloat(cs.opacity), p = el !== z ? parseFloat(getComputedStyle(el).opacity) : 1;
      const m = (cs.color.match(/[0-9.]+/g) || [0, 0, 0]).map(Number);
      const hell = (0.299 * m[0] + 0.587 * m[1] + 0.114 * m[2]) / 255;
      const a = (isNaN(o) ? 1 : o) * (isNaN(p) ? 1 : p) * (1 - hell);
      sig.push(re.left, re.top, re.width, re.height, a.toFixed(3), txt, cs.fontSize);
      return { re, cs, txt, a };
    });
    sig = sig.join('|'); if (sig === self.hintenSig) return; self.hintenSig = sig;
    const k = c.width / Math.max(1, rc.width);
    if (!self.hintenCv) self.hintenCv = document.createElement('canvas');
    const hc = self.hintenCv; hc.width = c.width; hc.height = c.height;
    const g = hc.getContext('2d'); g.clearRect(0, 0, hc.width, hc.height);
    let summe = 0;
    info.forEach((i) => {
      if (!i.txt || i.a <= 0.01) return; summe = Math.max(summe, i.a);
      const cs = i.cs, re = i.re;
      const fs = parseFloat(cs.fontSize) * k, lh = (parseFloat(cs.lineHeight) || parseFloat(cs.fontSize)) * k;
      g.font = cs.fontWeight + ' ' + fs + 'px ' + cs.fontFamily;
      try { g.letterSpacing = (parseFloat(cs.letterSpacing) || 0) * k + 'px'; } catch (e) { /* ältere Browser ohne Laufweite */ }
      g.textAlign = 'center'; g.textBaseline = 'alphabetic'; g.fillStyle = 'black'; g.globalAlpha = Math.min(1, i.a);
      const m = g.measureText(i.txt), fa = m.fontBoundingBoxAscent || fs * 0.8, fd = m.fontBoundingBoxDescent || fs * 0.2;
      g.fillText(i.txt, (re.left + re.width / 2 - rc.left) * k, (re.top - rc.top) * k + (lh - (fa + fd)) / 2 + fa);
    });
    g.globalAlpha = 1;
    const gl = self.gl;
    gl.activeTexture(gl.TEXTURE6); gl.bindTexture(gl.TEXTURE_2D, self.hintenTex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, hc);
    self.parameter(false);
    self.hinten = summe > 0 ? 1 : 0;
  }

  function Reihe(canvas, opt) {
    this.canvas = canvas; this.opt = opt || {}; this.slots = []; this.dreh = 0; this.tempo = 0; this.lebt = true;
    this.tex = {}; this.laedt = {}; this.hinten = 0;
    const gl = canvas.getContext('webgl', { premultipliedAlpha: true, alpha: true, antialias: true });
    this.gl = gl;
    if (!gl) { this.ohne = true; return; }
    const sh = (t, src) => { const s = gl.createShader(t); gl.shaderSource(s, src); gl.compileShader(s); return s; };
    const pr = gl.createProgram(); gl.attachShader(pr, sh(gl.VERTEX_SHADER, VS)); gl.attachShader(pr, sh(gl.FRAGMENT_SHADER, FS)); gl.linkProgram(pr); gl.useProgram(pr);
    if (!gl.getProgramParameter(pr, gl.LINK_STATUS)) { this.ohne = true; return; }
    const buf = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(pr, 'p'); gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    [['uLab0', LAB0 / BH], ['uLab1', LAB1 / BH], ['uCx', (CX + 0.5) / BW], ['uGlas', GLAS / BH], ['uGlasA', GLAS_A / BH], ['uCap0', CAP0 / BH], ['uCap1', CAP1 / BH], ['uLinse', 0.6]]
      .forEach((u) => gl.uniform1f(gl.getUniformLocation(pr, u[0]), u[1]));
    ['tFlasche', 'tEtikett', 'tProfil', 'tLicht', 'tKappe', 'tKappeLicht', 'tHinten'].forEach((n, i) => gl.uniform1i(gl.getUniformLocation(pr, n), i));
    this.uDreh = gl.getUniformLocation(pr, 'uDreh'); this.uKappe = gl.getUniformLocation(pr, 'uKappe'); this.kappeDreht = 1; gl.uniform1f(this.uKappe, 1); this.uHR = gl.getUniformLocation(pr, 'uHR'); this.uHinten = gl.getUniformLocation(pr, 'uHinten'); this.uWisch = gl.getUniformLocation(pr, 'uWisch'); this.wisch = [];
    /* Dauer eines Bildes auf diesem Gerät (60 Hz: 16,7 ms, 120 Hz: 8,3 ms), aus fünf Bildern gemessen */
    this.bild = 16.7; { let n = 0, t0 = 0; const m = (t) => { if (!n) t0 = t; if (++n < 6) requestAnimationFrame(m); else this.bild = Math.min(34, Math.max(6, (t - t0) / 5)); }; requestAnimationFrame(m); }
    this.hintenTex = gl.createTexture();
    gl.enable(gl.BLEND); gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    const h = this.opt.hilfen;
    Promise.all([ladeBild(h.profil), ladeBild(h.licht), ladeBild(h.kappe), ladeBild(h.kappeLicht)]).then((r) => {
      this.hilf = [this.mach(r[0]), this.mach(r[1]), this.mach(r[2], true), this.mach(r[3])];
      this.zeichne();
    }).catch(() => {});
    this.groesse();
    this.ro = new ResizeObserver(() => { this.groesse(); this.zeichne(); });
    this.ro.observe(canvas);
    if (this.opt.griff) this.greifen(this.opt.griff);
  }
  Reihe.prototype.parameter = function (wdh) {
    const gl = this.gl;
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, wdh ? gl.REPEAT : gl.CLAMP_TO_EDGE); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  };
  Reihe.prototype.mach = function (img, wdh) {
    const gl = this.gl, t = gl.createTexture(); gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, t);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
    this.parameter(wdh); return t;
  };
  /* Bilder einer Sorte laden, einmal */
  Reihe.prototype.lade = function (sorten) {
    if (this.ohne) return Promise.resolve();
    return Promise.all([].concat(sorten).map((key) => {
      const b = this.opt.bilder[key]; if (!b) return Promise.resolve();
      if (!this.laedt[key]) {
        this.laedt[key] = Promise.all([ladeBild(b.flasche), ladeBild(b.maske), ladeBild(b.etikett), ladeBild(this.opt.hilfen.etikettAlpha)]).then((r) => {
          this.tex[key] = [this.mach(zusammen(r[0], r[1])), this.mach(zusammen(r[2], r[3]), true)];
          this.zeichne();
        }).catch(() => {});
      }
      return this.laedt[key];
    }));
  };
  Reihe.prototype.groesse = function () {
    const c = this.canvas, d = Math.min(window.devicePixelRatio || 1, 2);
    const w = Math.round(c.clientWidth * d), h = Math.round(c.clientHeight * d);
    if (w && h && (c.width !== w || c.height !== h)) { c.width = w; c.height = h; this.hintenSig = ''; }
  };
  Reihe.prototype.setze = function (slots) { this.slots = slots; this.zeichne(); };
  Reihe.prototype.hinterText = function (el) { this.hintenEl = el; this.hintenSig = ''; this.zeichne(); };
  Reihe.prototype.drehe = function (x) { this.dreh += x; this.zeichne(); };
  /* Kronkorken mitdrehen (true) oder stehen lassen wie im Foto (false) */
  Reihe.prototype.kappe = function (an) { this.kappeDreht = an ? 1 : 0; if (this.gl && !this.ohne) { this.gl.uniform1f(this.uKappe, this.kappeDreht); this.zeichne(); } };
  Reihe.prototype.zeichne = function () {
    const gl = this.gl, c = this.canvas; if (!gl || this.ohne || !this.hilf || !this.lebt) return;
    const d = c.width / Math.max(1, c.clientWidth), W = Math.max(1, c.clientWidth), H = Math.max(1, c.clientHeight);
    gl.viewport(0, 0, c.width, c.height); gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT);
    hintenMaske(this, this.hintenEl, c);
    for (let i = 0; i < 4; i++) { gl.activeTexture(gl.TEXTURE2 + i); gl.bindTexture(gl.TEXTURE_2D, this.hilf[i]); }
    gl.activeTexture(gl.TEXTURE6); gl.bindTexture(gl.TEXTURE_2D, this.hintenTex); gl.uniform1f(this.uHinten, this.hinten);
    this.slots.forEach((s, i) => {
      const t = this.tex[s.sorte]; if (!t || s.h <= 0) return;
      const w = s.h * BW / BH;
      if (s.x + w / 2 < 0 || s.x - w / 2 > W) return;
      gl.viewport(Math.round((s.x - w / 2) * d), Math.round(c.height - (s.y + s.h) * d), Math.round(w * d), Math.round(s.h * d));
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, t[0]);
      gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, t[1]);
      const ang = this.dreh + (s.dreh || 0);
      gl.uniform1f(this.uDreh, ang);
      gl.uniform1f(this.uWisch, this.wischen(i, ang));
      gl.uniform4f(this.uHR, (s.x - w / 2) / W, s.y / H, w / W, s.h / H);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    });
  };
  /* Weg des Deckels je gezeigtem Bild, für die Unschärfe. Gezählt wird der Sprung zwischen zwei gezeigten Bildern, nicht das
     Tempo pro Zeit: lässt ein Gerät Bilder aus, springt der Deckel weiter, und genau das sieht das Auge. Mehrere Aufrufe innerhalb
     eines Bildes (this.bild, gemessen) zählen zusammen. Weich gemittelt, steht die Flasche, klingt der Wert in wenigen Bildern ab. */
  Reihe.prototype.wischen = function (i, ang) {
    const jetzt = performance.now(); let w = this.wisch[i];
    if (!w) w = this.wisch[i] = { a: ang, t: jetzt, v: 0 };
    const dt = jetzt - w.t;
    if (dt >= this.bild * 0.75) {
      const roh = dt <= 250 ? ang - w.a : 0;
      w.v += (roh - w.v) * 0.35; /* ruhig gemittelt: der Daumen liefert ungleichmäßige Sprünge, die Unschärfe soll nicht pulsieren */
      if (Math.abs(w.v) < 0.0005) w.v = 0;
      w.a = ang; w.t = jetzt;
    }
    if (w.v && !this.wischPlan) this.wischPlan = requestAnimationFrame(() => { this.wischPlan = 0; this.zeichne(); });
    return w.v;
  };
  /* Ziehen: der Punkt unter dem Finger bleibt unter dem Finger. Loslassen: Schwung, der ausläuft. Tippen ist kein Ziehen. */
  Reihe.prototype.greifen = function (el) {
    let x0 = 0, y0 = 0, letzt = 0, tl = 0, aktiv = false, entschieden = false, waagerecht = false, id = null;
    const radius = () => Math.max(40, (this.radiusPx ? this.radiusPx() : this.canvas.clientWidth * 0.2));
    el.addEventListener('pointerdown', (e) => {
      aktiv = true; entschieden = false; waagerecht = false; id = e.pointerId;
      x0 = letzt = e.clientX; y0 = e.clientY; tl = performance.now(); this.tempo = 0;
    }, { passive: true });
    el.addEventListener('pointermove', (e) => {
      if (!aktiv || e.pointerId !== id) return;
      const dx = e.clientX - x0, dy = e.clientY - y0;
      if (!entschieden) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        entschieden = true; waagerecht = Math.abs(dx) > Math.abs(dy) * 1.2;
        if (waagerecht) { try { el.setPointerCapture(id); } catch (x) { /* ohne Capture geht es auch */ } this.gezogen = true; }
      }
      if (!waagerecht) return;
      const t = performance.now(), dd = (e.clientX - letzt) / radius();
      this.dreh += dd; this.tempo = this.tempo * 0.6 + (dd / Math.max(8, t - tl)) * 0.4 * 16.7;
      letzt = e.clientX; tl = t; this.zeichne();
    }, { passive: true });
    const los = () => {
      if (!aktiv) return; aktiv = false;
      if (!waagerecht) return;
      setTimeout(() => { this.gezogen = false; }, 0);
      if (performance.now() - tl > 90) this.tempo = 0;
      if (window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches) this.tempo = 0;
      this.auslaufen();
    };
    el.addEventListener('pointerup', los, { passive: true }); el.addEventListener('pointercancel', los, { passive: true });
  };
  Reihe.prototype.auslaufen = function () {
    if (this.laeuft) return; this.laeuft = true;
    const f = () => {
      if (!this.lebt) return;
      this.tempo *= 0.95; this.dreh += this.tempo; this.zeichne();
      if (Math.abs(this.tempo) > 0.0004) requestAnimationFrame(f); else this.laeuft = false;
    };
    requestAnimationFrame(f);
  };
  Reihe.prototype.weg = function () { this.lebt = false; if (this.ro) this.ro.disconnect(); };

  window.zlFlasche = { Reihe };
})();
